import { Space, Spin, Steps } from "antd"
import { useCallback, useEffect, useState } from "react"
import { DataUpload } from "./DataUpload"
import { MapSheets } from "./MapSheets"
import { MapColumns } from "./MapColumns"
import { SelectExperiments } from "./SelectExperiments"
import { useDispatch, useSelector } from "react-redux"
import { fileProcessClear, fileProcessRequest, uploadFileRequest } from "src/store/actions/files"
import { StoreState } from "src/store/configureStore"
import { AsyncStates } from "src/constants"
import { LoadingOutlined } from "@ant-design/icons"
import { Prompt } from "react-router-dom"
import { MapUnits } from "./MapUnits"
import useTranslate from "src/utils/useTranslate"
import { fetchAllMethodsRequest, fetchPropertyRequest } from "src/store/actions/repository"
import { useVariableValue } from "@devcycle/devcycle-react-sdk"
import { addRemoveIngredientsKeyToWorkOrderClear } from "src/store/actions/workOrderDetails"
import { findArchivedParametersClear } from "src/store/actions/common"

const { Step } = Steps


export const DataMapper = ({ workOrder, currentExpIdIndex, unsaved, setUnsaved }: any) => {
	const dispatch = useDispatch()
	const enablePropertyStandards = useVariableValue('property-standards', false)
	const fileUploadStatus = useSelector((state: StoreState) => state.files.uploadFile.status)
	const fileProcessData = useSelector((state: StoreState) => state.files.fileProcess.data)
	const fileProcessStatus = useSelector((state: StoreState) => state.files.fileProcess.status)

	const [current, setCurrent] = useState(0)
	const [file, setFile] = useState<any>()
	const [sheets, setSheets] = useState<any>([])
	const [tableData, setTableData] = useState<any>({})
	const [previewData, setPreviewData] = useState<any>({})
	const [unitsData, setUnitsData] = useState<any>([])
	const [processSheets, setProcessSheets] = useState<any>(false)
	const [processColumns, setProcessColumn] = useState<any>(false)
	const [processExps, setProcessExps] = useState<any>(false)
	const [fileData, setFileData] = useState<any>(null)
	const [parameterMapping, setParameterMapping] = useState<any>({ formulations: [], processing: [], characterizations: [], properties: [] })
	const [uploadFormat, setUploadFormat] = useState("default")
	const [t] = useTranslate()

	useEffect(() => {
		if (enablePropertyStandards) {
			dispatch(fetchPropertyRequest())
			dispatch(fetchAllMethodsRequest())
		}
	}, [dispatch, enablePropertyStandards])

	useEffect(() => {
		if (fileUploadStatus === AsyncStates.SUCCESS) {
			setUnsaved(false)
			dispatch(addRemoveIngredientsKeyToWorkOrderClear())
			dispatch(findArchivedParametersClear())
		}
	}, [fileUploadStatus, setUnsaved, dispatch])

	const getColumnMappingKey = useCallback((value: string, type: string) => {
		const updatedValue = String(value)?.trim()?.toLowerCase()
		if (type === "experiments") {
			if (updatedValue?.includes("experiment")) {
				return "experiments"
			}
		}
		else if (type === "formulations") {
			if (updatedValue?.includes("category")) {
				return "ingredients_category"
			}
			else if (updatedValue?.includes("unit")) {
				return "ingredients_unit"
			}
			else if (updatedValue?.includes("ingredient")) {
				return "ingredients_parameters"
			}
		}
		else if (type === "processing") {
			if (updatedValue?.includes("category")) {
				return "processing_category"
			}
			else if (updatedValue?.includes("unit")) {
				return "processing_unit"
			}
			else if (updatedValue?.includes("process")) {
				return "processing_parameters"
			}
		}
		else if (type === "characterizations") {
			if (updatedValue?.includes("category")) {
				return "characterizations_category"
			}
			else if (updatedValue?.includes("unit")) {
				return "characterizations_unit"
			}
			else if (updatedValue?.includes("character")) {
				return "characterizations_parameters"
			}
		}
		else if (type === "properties") {
			if (updatedValue?.includes("category")) {
				return "properties_category"
			}
			else if (updatedValue?.includes("unit")) {
				return "properties_unit"
			}
			else if (updatedValue?.includes("standard")) {
				return "properties_standard"
			}
			else if (updatedValue?.includes("spec")) {
				return "properties_specimen"
			}
			else if (updatedValue?.includes("method")) {
				return "properties_method"
			}
			else if (updatedValue?.includes("proper")) {
				return "properties_parameters"
			}
		}
		return null
	}, [])

	useEffect(() => {
		if (!!file) {
			setSheets([])
			setTableData({})
			setPreviewData({})
			setUnitsData([])
			setFileData(null)
			dispatch(fileProcessClear())
		}
		return () => {
			dispatch(findArchivedParametersClear())
			dispatch(fileProcessClear())
		}
	}, [dispatch, file, uploadFormat])

	const selectSheets = useCallback(() => {
		setProcessSheets(true)
		const formData = new FormData()
		formData.append("upload_type", uploadFormat)
		formData.append("work_order_id", workOrder?.work_order_id)
		formData.append("mapping_type", "sheets")
		formData.append("file", file)
		dispatch(fileProcessRequest(formData))
	}, [dispatch, file, workOrder?.work_order_id, uploadFormat])

	const selectColumns = useCallback(() => {
		setProcessColumn(true)
		const formData = new FormData()
		formData.append("upload_type", uploadFormat)
		formData.append("work_order_id", workOrder?.work_order_id)
		formData.append("mapping_type", "columns")
		if (!!fileData?.fileId && !!fileData?.filename) {
			formData.append("file_id", fileData?.fileId)
			formData.append("file_name", fileData?.filename)
		} else {
			formData.append("file", file)
		}
		const mappedSheets = sheets.reduce((acc: any, curr: any) => ({ ...acc, [curr?.sheet_mapped_to]: curr?.sheet_name }), {})
		formData.append("mapped_sheets", JSON.stringify(mappedSheets))
		dispatch(fileProcessRequest(formData))
	}, [dispatch, file, fileData?.fileId, fileData?.filename, sheets, workOrder?.work_order_id, uploadFormat])

	const selectExperiments = useCallback(() => {
		setProcessExps(true)
		const formData = new FormData()
		formData.append("upload_type", uploadFormat)
		formData.append("work_order_id", workOrder?.work_order_id)
		formData.append("mapping_type", "experiments")
		formData.append("data_type", "formulations")
		if (!!fileData?.fileId && !!fileData?.filename) {
			formData.append("file_id", fileData?.fileId)
			formData.append("file_name", fileData?.filename)
		} else {
			formData.append("file", file)
		}
		const mappedSheets = sheets.reduce((acc: any, curr: any) => ({ ...acc, [curr?.sheet_mapped_to]: curr?.sheet_name }), {})
		formData.append("mapped_sheets", JSON.stringify(mappedSheets))
		const mappedColumns = {
			experiments: tableData?.experiments?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns_mapped_to]: curr?.columns
			}), {}),
			formulations: tableData?.formulations?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {}),
			processing: tableData?.processing?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {}),
			characterizations: tableData?.characterizations?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {}),
			properties: tableData?.properties?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {})
		}
		formData.append("mapped_columns", JSON.stringify(mappedColumns))
		dispatch(fileProcessRequest(formData))
	}, [dispatch, file, fileData?.fileId, fileData?.filename, sheets, tableData, workOrder?.work_order_id, uploadFormat])

	useEffect(() => {
		if (fileProcessStatus === AsyncStates.SUCCESS && !!fileProcessData?.file_id && !!fileProcessData?.file_name) {
			setFileData({ fileId: fileProcessData?.file_id, filename: fileProcessData?.file_name })
		}
	}, [fileProcessStatus, fileProcessData?.file_id, fileProcessData?.file_name])

	useEffect(() => {
		if (fileProcessStatus === AsyncStates.SUCCESS && !!fileProcessData?.sheets?.length && processSheets) {
			setProcessSheets(false)
			setSheets(fileProcessData?.sheets.map((element: any) => {
				const stageData = workOrder.stages?.[currentExpIdIndex]?.data
				const trimmedElement = element?.trim()?.toLowerCase()
				const sheetRow: any = { sheet_name: element }
				if (trimmedElement?.includes("experi")) {
					sheetRow.sheet_mapped_to = "experiments"
				}
				else if (trimmedElement?.includes("form") && stageData.find((res: any) => res?.type === "formulation_stage")) {
					sheetRow.sheet_mapped_to = "formulations"
				}
				else if (trimmedElement?.includes("process") && stageData.find((res: any) => res?.type === "processing_stage")) {
					sheetRow.sheet_mapped_to = "processing"
				}
				else if (trimmedElement?.includes("char") && stageData.find((res: any) => res?.type === "characterization_stage")) {
					sheetRow.sheet_mapped_to = "characterizations"
				}
				else if (trimmedElement?.includes("prop") && stageData.find((res: any) => res?.type === "properties_stage")) {
					sheetRow.sheet_mapped_to = "properties"
				}
				return sheetRow
			}))
			setUnsaved(true)
			setCurrent(1)
		}
	}, [fileProcessStatus, fileProcessData, processSheets, setUnsaved, currentExpIdIndex, workOrder?.stages])

	useEffect(() => {
		if (fileProcessStatus === AsyncStates.SUCCESS && !!Object.keys(fileProcessData?.columns || {})?.length && processColumns) {
			setProcessColumn(false)
			setTableData(() => {
				const newState: any = {}
				newState.experiments = fileProcessData?.columns?.experiments?.filter((res: any) => !!res).map((res: any) => ({
					columns: res,
					columns_mapped_to: getColumnMappingKey(res, "experiments"),
				}))
				const stageData = workOrder.stages?.[currentExpIdIndex]?.data
				if (stageData.find((res: any) => res?.type === "formulation_stage")) {
					newState.formulations = fileProcessData?.columns?.formulations?.filter((res: any) => !!res).map((res: any) => ({
						columns: res,
						columns_mapped_to: getColumnMappingKey(res, "formulations"),
					}))
				}
				if (stageData.find((res: any) => res?.type === "processing_stage")) {
					newState.processing = fileProcessData?.columns?.processing?.filter((res: any) => !!res).map((res: any) => ({
						columns: res,
						columns_mapped_to: getColumnMappingKey(res, "processing"),
					}))
				}
				if (stageData.find((res: any) => res?.type === "characterization_stage")) {
					newState.characterizations = fileProcessData?.columns?.characterizations?.filter((res: any) => !!res).map((res: any) => ({
						columns: res,
						columns_mapped_to: getColumnMappingKey(res, "characterizations"),
					}))
				}
				if (stageData.find((res: any) => res?.type === "properties_stage")) {
					newState.properties = fileProcessData?.columns?.properties?.filter((res: any) => !!res).map((res: any) => ({
						columns: res,
						columns_mapped_to: getColumnMappingKey(res, "properties"),
					}))

				}
				return newState
			})
			setCurrent(2)
		}
	}, [fileProcessStatus, fileProcessData, tableData, processColumns, getColumnMappingKey, workOrder.stages, currentExpIdIndex])

	useEffect(() => {
		if (fileProcessStatus === AsyncStates.SUCCESS && !!Object.keys(fileProcessData?.experiments || {})?.length && processExps) {
			setProcessExps(false)
			setParameterMapping({ formulations: [], processing: [], characterizations: [], properties: [] })
			setPreviewData(fileProcessData?.experiments)
			setUnitsData([...new Set(Object.values(fileProcessData?.experiments || {}).flatMap((value: any) => value?.units || []))]
				?.map((res: any) => ({ unit_name: res })))
			setCurrent(3)
		}
	}, [fileProcessStatus, fileProcessData, processExps])

	const getComponent = () => {
		switch (current) {
			case 0:
				return (
					<DataUpload
						data={{ file, uploadFormat }}
						callbacks={{ setFile, setUnsaved, selectSheets, setUploadFormat, selectExperiments }}
					/>
				)
			case 1:
				return (
					<MapSheets
						data={{ sheets, filename: fileData?.filename, stage: workOrder.stages?.[currentExpIdIndex] }}
						callbacks={{ setCurrent, setSheets, selectColumns }}
					/>
				)
			case 2:
				return (
					<MapColumns
						data={{ tableData }}
						callbacks={{ setCurrent, setTableData, selectExperiments }}
					/>
				)
			case 3:
				return (
					<SelectExperiments
						data={{ workOrderId: workOrder?.work_order_id, unitsData, uploadFormat, previewData, parameterMapping }}
						callbacks={{ setCurrent, setUnitsData, setParameterMapping, uploadData, }}
					/>
				)
			case 4:
				return (
					<MapUnits
						data={{ unitsData }}
						callbacks={{ setUnitsData, setCurrent, uploadData }}
					/>
				)
			default:
				return ""
		}
	}

	const uploadData = useCallback(() => {
		const formData = new FormData()
		formData.append("upload_type", uploadFormat)
		formData.append("work_order_id", workOrder?.work_order_id)
		formData.append("experiment_id", workOrder?.experiment_id?.[currentExpIdIndex])
		formData.append("stage", workOrder?.stages?.[currentExpIdIndex]?.identifier)
		formData.append("remove_ingredient_ids", workOrder?.remove_ingredient_ids ?? [])
		formData.append("parameter_mapping", JSON.stringify(parameterMapping))
		formData.append("data_type", "formulations")
		if (!!fileData?.fileId && !!fileData?.filename) {
			formData.append("file_id", fileData?.fileId)
			formData.append("file_name", fileData?.filename)
		} else {
			formData.append("file", file)
		}
		const mappedSheets = sheets.reduce((acc: any, curr: any) => ({ ...acc, [curr?.sheet_mapped_to]: curr?.sheet_name }), {})
		formData.append("mapped_sheets", JSON.stringify(mappedSheets))
		const mappedColumns = {
			experiments: tableData?.experiments?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns_mapped_to]: curr?.columns
			}), {}),
			formulations: tableData?.formulations?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {}),
			processing: tableData?.processing?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {}),
			characterizations: tableData?.characterizations?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {}),
			properties: tableData?.properties?.reduce((acc: any, curr: any) => ({
				...acc, [curr?.columns]: curr?.columns_mapped_to
			}), {})
		}
		formData.append("mapped_columns", JSON.stringify(mappedColumns))
		if (!!unitsData?.length) {
			const mappedUnits = unitsData?.reduce((acc: any, curr: any) => ({ ...acc, [curr?.unit_name]: curr?.unit_mapped_to }), {})
			formData.append("mapped_units", JSON.stringify(mappedUnits))
		}
		dispatch(uploadFileRequest(formData))
	}, [
		dispatch,
		file,
		unitsData,
		currentExpIdIndex,
		workOrder,
		sheets,
		tableData,
		fileData?.fileId,
		fileData?.filename,
		uploadFormat,
		parameterMapping
	])


	return (
		<>
			<Prompt when={unsaved} message={t("dataMapper.fileNotUploadedYet")} />
			<Spin
				spinning={fileUploadStatus === AsyncStates.LOADING}
				indicator={<LoadingOutlined />}
			>
				<Space
					direction="vertical"
					style={{ width: "100%", padding: 20 }}
					size="large"
				>
					<Steps
						current={current}
						direction="horizontal"
					>
						<Step title={t("dataMapper.uploadFile")}></Step>
						{uploadFormat === "default" &&
							<Step title={t("dataMapper.mapsheets")}></Step>}
						{uploadFormat === "default" &&
							<Step title={t("dataMapper.mapColumns")}></Step>}
						<Step title={t("common.preview")}></Step>
						{!!unitsData?.length && (
							<Step title={t("common.mapUnits")}></Step>
						)}
					</Steps>
					{getComponent()}
				</Space>
			</Spin>
		</>
	)
}
